import React, { useState, useEffect, useCallback } from 'react';
import { useOrg } from '../context/OrgContext';
import { usePermissions } from '../components/PermissionGuard';
import { PageHeader } from '../components/PageHeader';
import * as mappingsApi from '../api/energyhubMappings';

export function EnergyHubMappings() {
  const { selectedOrg } = useOrg();
  const { hasPermission } = usePermissions();
  const canViewCompliance = hasPermission('compliance:read');
  const canAdmin = hasPermission('admin');
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({ deviceId: '', siteId: '' });
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all');

  const loadData = useCallback(async () => {
    if (!selectedOrg || !canViewCompliance) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const filters = {};
      if (filter === 'enrolled') filters.enrollmentStatus = 'enrolled';
      if (filter === 'pending') filters.enrollmentStatus = 'pending';
      if (filter === 'unmapped') filters.unmappedOnly = true;
      
      const result = await mappingsApi.getMappings(selectedOrg.id, filters);
      setMappings(result.mappings);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedOrg, canViewCompliance, filter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleEdit = (mapping) => {
    setEditingId(mapping.id);
    setEditValues({
      deviceId: mapping.energyhubDeviceId || '',
      siteId: mapping.energyhubSiteId || ''
    });
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditValues({ deviceId: '', siteId: '' });
  };

  const handleSave = async (mappingId) => {
    if (!selectedOrg || saving) return;
    setSaving(true);
    try {
      await mappingsApi.updateMapping(selectedOrg.id, mappingId, {
        energyhubDeviceId: editValues.deviceId || null,
        energyhubSiteId: editValues.siteId || null
      });
      setEditingId(null);
      loadData();
    } catch (err) {
      alert('Failed to save: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'enrolled': return { bg: '#c6f6d5', color: '#276749' };
      case 'pending': return { bg: '#fefcbf', color: '#975a16' };
      case 'error': return { bg: '#fed7d7', color: '#c53030' };
      default: return { bg: '#e2e8f0', color: '#4a5568' };
    }
  };

  if (!canViewCompliance) {
    return (
      <div style={styles.container}>
        <div style={styles.accessDenied}>
          <h2>Access Denied</h2>
          <p>You need compliance permissions to view EnergyHub mappings.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <PageHeader
        title="EnergyHub Device Mappings"
        subtitle="Map assets to EnergyHub identifiers"
      />

      <div style={styles.filterBar}>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Status</label>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={styles.filterSelect}
            data-testid="status-filter"
          >
            <option value="all">All</option>
            <option value="enrolled">Enrolled</option>
            <option value="pending">Pending</option>
            <option value="unmapped">Unmapped Only</option>
          </select>
        </div>
        <div style={styles.totalCount}>
          {mappings.length} mapping{mappings.length !== 1 ? 's' : ''}
        </div>
      </div>

      {error && (
        <div style={styles.error}>{error}</div>
      )}

      {loading ? (
        <div style={styles.loading}>Loading mappings...</div>
      ) : (
        <div style={styles.tableContainer}>
          <table style={styles.table} data-testid="mappings-table">
            <thead>
              <tr>
                <th style={styles.th}>Asset</th>
                <th style={styles.th}>Site</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>EnergyHub Device ID</th>
                <th style={styles.th}>EnergyHub Site ID</th>
                <th style={styles.th}>Last Sync</th>
                {canAdmin && <th style={styles.th}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {mappings.map(mapping => {
                const statusStyle = getStatusColor(mapping.enrollmentStatus);
                const isEditing = editingId === mapping.id;
                
                return (
                  <tr key={mapping.id} style={styles.tr} data-testid={`mapping-${mapping.id}`}>
                    <td style={styles.td}>
                      <div style={styles.assetName}>{mapping.assetName}</div>
                      <div style={styles.assetId}>{mapping.assetId}</div>
                    </td>
                    <td style={styles.td}>{mapping.siteName}</td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, background: statusStyle.bg, color: statusStyle.color }}>
                        {mapping.enrollmentStatus}
                      </span>
                    </td>
                    <td style={styles.td}>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editValues.deviceId}
                          onChange={(e) => setEditValues(prev => ({ ...prev, deviceId: e.target.value }))}
                          style={styles.input}
                          placeholder="eh-dev-xxx"
                          data-testid="device-id-input"
                        />
                      ) : (
                        <code style={styles.code}>{mapping.energyhubDeviceId || '-'}</code>
                      )}
                    </td>
                    <td style={styles.td}>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editValues.siteId}
                          onChange={(e) => setEditValues(prev => ({ ...prev, siteId: e.target.value }))}
                          style={styles.input}
                          placeholder="eh-site-xxx"
                          data-testid="site-id-input"
                        />
                      ) : (
                        <code style={styles.code}>{mapping.energyhubSiteId || '-'}</code>
                      )}
                    </td>
                    <td style={styles.td}>
                      {mapping.lastSyncAt ? new Date(mapping.lastSyncAt).toLocaleString() : '-'}
                    </td>
                    {canAdmin && (
                      <td style={styles.td}>
                        {isEditing ? (
                          <div style={styles.actionButtons}>
                            <button
                              onClick={() => handleSave(mapping.id)}
                              disabled={saving}
                              style={styles.saveButton}
                              data-testid="save-button"
                            >
                              {saving ? 'Saving...' : 'Save'}
                            </button>
                            <button
                              onClick={handleCancel}
                              style={styles.cancelButton}
                              data-testid="cancel-button"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleEdit(mapping)}
                            style={styles.editButton}
                            data-testid={`edit-${mapping.id}`}
                          >
                            Edit
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
              {mappings.length === 0 && (
                <tr>
                  <td colSpan={canAdmin ? 7 : 6} style={styles.emptyState}>
                    No mappings found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { padding: '2rem', maxWidth: '1400px', margin: '0 auto' },
  accessDenied: { padding: '3rem', textAlign: 'center', background: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  filterBar: { display: 'flex', gap: '1rem', marginBottom: '1rem', alignItems: 'flex-end' },
  filterGroup: { display: 'flex', flexDirection: 'column', gap: '0.25rem' },
  filterLabel: { fontSize: '0.75rem', fontWeight: '500', color: '#4a5568' },
  filterSelect: { padding: '0.5rem', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.875rem', background: 'white', minWidth: '150px' },
  totalCount: { marginLeft: 'auto', fontSize: '0.875rem', color: '#718096' },
  error: { padding: '1rem', background: '#fed7d7', color: '#c53030', borderRadius: '8px', marginBottom: '1rem' },
  loading: { padding: '2rem', textAlign: 'center', color: '#718096' },
  tableContainer: { background: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '0.75rem 1rem', background: '#f7fafc', borderBottom: '1px solid #e2e8f0', fontWeight: '600', fontSize: '0.75rem', color: '#4a5568', textTransform: 'uppercase' },
  tr: { borderBottom: '1px solid #e2e8f0' },
  td: { padding: '0.75rem 1rem', fontSize: '0.875rem' },
  assetName: { fontWeight: '500' },
  assetId: { fontSize: '0.75rem', color: '#718096' },
  badge: { padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', textTransform: 'capitalize' },
  code: { background: '#f7fafc', padding: '0.125rem 0.25rem', borderRadius: '2px', fontSize: '0.75rem' },
  input: { padding: '0.375rem 0.5rem', borderRadius: '4px', border: '1px solid #e2e8f0', fontSize: '0.75rem', width: '120px' },
  actionButtons: { display: 'flex', gap: '0.5rem' },
  editButton: { padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid #3182ce', background: 'white', color: '#3182ce', fontSize: '0.75rem', cursor: 'pointer' },
  saveButton: { padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid #38a169', background: '#38a169', color: 'white', fontSize: '0.75rem', cursor: 'pointer' },
  cancelButton: { padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid #e2e8f0', background: 'white', fontSize: '0.75rem', cursor: 'pointer' },
  emptyState: { padding: '2rem', textAlign: 'center', color: '#718096' }
};
